import {differenceBy} from 'lodash';
import {connect} from 'react-redux';

import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {fetchApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {AsyncSearchDropdownDataFetcher} from '../../../../../libs/shared/ui/src/components/dropdowns/search/asyncSearchDropdown';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {buildTeammateSearchUrl} from '../../../../helpers/models/teammateHelpers';
import {ApiSearchTeammates} from '../../../../models/api/searchTeammateModel';
import {AppState} from '../../../../store/appState';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {getUserId} from '../../../../store/data/teammates/teammatesSelectors';
import {getUserTeamIds} from '../../../../store/data/teams/teamsSelectors';
import {ToggleDropdownTeammateHandler} from '../editor/context/types';
import {WorkspaceTeammatesDropdown} from './workspaceTeammatesDropdown';

/*
 * Props.
 */

interface WorkspaceTeammatesDropdownContainerProps {
  panelId: InboxesPanelId;
  onToggleDropdownTeammate: ToggleDropdownTeammateHandler;
  displayedTeammateIds: Array<number>;
  teammatesDropdownSelectedIds: Array<number>;
}

interface PaginationState {
  nextPageIndex: number;
}

/*
 * Selectors.
 */

function getDisplayedTeammateIds(_state: AppState, props: WorkspaceTeammatesDropdownContainerProps) {
  return props.displayedTeammateIds;
}

function getTeammatesDropdownSelectedIds(_state: AppState, props: WorkspaceTeammatesDropdownContainerProps) {
  return props.teammatesDropdownSelectedIds;
}

function makeGetFetchTeammatesAsync() {
  return createSelector(
    [getCompanyUrl, getUserTeamIds, getDisplayedTeammateIds, getTeammatesDropdownSelectedIds, getUserId],
    (
      companyUrl,
      teamIds,
      displayedTeammateIds,
      teammatesDropdownSelectedIds,
      userId,
    ): AsyncSearchDropdownDataFetcher<TeammateModel, PaginationState> =>
      async (query, teammates, cancelToken, paginationData) => {
        // Build the URL and options.
        const nextPageIndex = paginationData?.nextPageIndex || 0;
        const url = buildTeammateSearchUrl(companyUrl, query, {
          offset: nextPageIndex,
          teamIds,
        });

        // Fetch the teammates.
        const fetchedTeammates = await fetchApiAsync(ApiSearchTeammates, url, {cancelToken});
        const canLoadMore = fetchedTeammates.teammates.length === fetchedTeammates.limit;

        // Filter to new teammates since selected are already there if there's no query.
        const newTeammates = query
          ? fetchedTeammates.teammates
          : differenceBy(fetchedTeammates.teammates, teammates, (t) => t?.id);
        const allTeammates = teammates.concat(newTeammates);

        const allHiddenTeammates = allTeammates.reduce((result: Array<TeammateModel>, teammate) => {
          if (!teammate) {
            return result;
          }

          // Exclude the user's own teammate.
          if (teammate.id === userId) {
            return result;
          }

          // Whether the teammate was hidden before
          const isTeammateAlreadyHidden = !displayedTeammateIds.includes(teammate.id);

          // Whether the teammate is currently selected in the dropdown
          const isTeammateCurrentlySelected = teammatesDropdownSelectedIds.includes(teammate.id);

          if (isTeammateAlreadyHidden || isTeammateCurrentlySelected) {
            result.push(teammate);
          }
          return result;
        }, []);

        return {
          data: allHiddenTeammates,
          canRequestMore: canLoadMore,
          paginationData: {
            nextPageIndex: nextPageIndex + fetchedTeammates.teammates.length,
          },
        };
      },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<WorkspaceTeammatesDropdownContainerProps>()(() => ({
  fetchTeammatesAsync: makeGetFetchTeammatesAsync(),
}));

/**
 * This component is adapted from the now-removed <TeammatesManagerDropdownContainer /> but for use in <WorkspacesEditor />.
 */
export const WorkspacesEditorTeammatesDropdownContainer = connect(
  mapStateToProps,
  {},
  (stateProps, dispatchProps, ownProps) => {
    const toggleTeammate = (teammate: TeammateModel) => {
      ownProps.onToggleDropdownTeammate(teammate);
    };

    return {
      ...stateProps,
      ...ownProps,
      toggleTeammate,
    };
  },
)(WorkspaceTeammatesDropdown);
